import { useEffect } from "react";
import { BrandMark } from "../BrandMark";
import { brandDirectory, orderedBrandDirectory } from "../../data/brandDirectory";
import { useLocale } from "../../i18n/LocaleContext";
import { buildLocalizedPath } from "../../lib/siteRouting";

export function BrandIndexPage() {
  const { locale } = useLocale();
  const categoryGroups = Array.from(new Set(brandDirectory.map((brand) => brand.category)));
  const marketCount = Array.from(new Set(brandDirectory.flatMap((brand) => brand.markets))).length;

  const labels = {
    en: {
      back: "Back to Home",
      eyebrow: "Brand Portfolio",
      title: "A focused K-Beauty portfolio for global partners.",
      body: "Selected Korean beauty brands for distribution and market-entry conversations.",
      brands: "Brands",
      roles: "Roles",
      marketsTotal: "Markets",
      filters: "Portfolio Roles",
      portfolioBrand: "Portfolio Brand",
      brandReference: "Brand Reference",
      featuredVisual: "Featured Visual",
      market: "Market",
      role: "Role",
      view: "View Brand",
    },
    zh: {
      back: "返回首页",
      eyebrow: "品牌组合",
      title: "面向全球合作伙伴的韩国美妆品牌组合。",
      body: "精选韩国美妆品牌，用于代理、分销与市场拓展沟通。",
      brands: "品牌",
      roles: "角色",
      marketsTotal: "市场",
      filters: "品牌角色",
      portfolioBrand: "品牌组合",
      brandReference: "品牌参考",
      featuredVisual: "品牌视觉",
      market: "市场",
      role: "角色",
      view: "查看品牌",
    },
    ko: {
      back: "홈으로 돌아가기",
      eyebrow: "브랜드 포트폴리오",
      title: "글로벌 파트너를 위한 K-뷰티 포트폴리오.",
      body: "유통과 시장 진출 논의에 적합한 한국 뷰티 브랜드를 선별해 소개합니다.",
      brands: "브랜드",
      roles: "역할",
      marketsTotal: "시장",
      filters: "포트폴리오 역할",
      portfolioBrand: "브랜드 포트폴리오",
      brandReference: "브랜드 레퍼런스",
      featuredVisual: "브랜드 비주얼",
      market: "시장",
      role: "역할",
      view: "브랜드 보기",
    },
    vi: {
      back: "Quay lại trang chủ",
      eyebrow: "Danh mục thương hiệu",
      title: "Danh mục K-Beauty tập trung cho đối tác toàn cầu.",
      body: "Các thương hiệu beauty Hàn Quốc được chọn lọc cho phân phối và mở thị trường.",
      brands: "Thương hiệu",
      roles: "Vai trò",
      marketsTotal: "Thị trường",
      filters: "Vai trò danh mục",
      portfolioBrand: "Danh mục thương hiệu",
      brandReference: "Tham chiếu thương hiệu",
      featuredVisual: "Hình ảnh thương hiệu",
      market: "Thị trường",
      role: "Vai trò",
      view: "Xem thương hiệu",
    },
  }[locale];

  useEffect(() => {
    const canonical = `https://www.qiandu.group${buildLocalizedPath(locale, "/brands")}`;
    document.title = `${labels.eyebrow} | QIANDU`;
    const canonicalLink = document.head.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute("href", canonical);
    }
  }, [labels.eyebrow, locale]);

  return (
    <main className="px-5 pb-20 pt-24 sm:px-8 lg:px-12 lg:pb-24 lg:pt-32">
      <div className="mx-auto max-w-[88rem]">
        <a
          href={buildLocalizedPath(locale, "/")}
          className="inline-flex rounded-full border border-[#dce8ee] bg-[rgba(251,254,255,0.82)] px-5 py-3 text-[11px] uppercase tracking-[0.22em] text-soft/64 shadow-[0_10px_22px_rgba(106,138,151,0.06)] backdrop-blur"
        >
          {labels.back}
        </a>

        <div className="mt-10 max-w-[27rem]">
          <div className="text-[0.68rem] uppercase tracking-[0.5em] text-aurora/72">{labels.eyebrow}</div>
          <h1 className={`${locale === "zh" || locale === "vi" ? "font-sans text-[1.96rem] font-medium tracking-[-0.01em] sm:text-[2.62rem]" : "font-display text-[2.12rem] sm:text-[2.98rem]"} mt-5 leading-[1.02] text-[#1f2b31]`}>
            {labels.title}
          </h1>
          <h2 className="mt-4 text-[0.78rem] font-medium uppercase tracking-[0.28em] text-soft/48">{labels.portfolioBrand}</h2>
          <p className="mt-5 max-w-[14.5rem] text-[0.84rem] leading-7 text-soft/52">{labels.body}</p>
        </div>

        <div className="mt-10 grid gap-3 sm:grid-cols-3">
          {[
            [brandDirectory.length, labels.brands],
            [categoryGroups.length, labels.roles],
            [marketCount, labels.marketsTotal],
          ].map(([value, label]) => (
            <div
              key={label}
              className="rounded-[2.1rem] border border-[#dce8ee] bg-[linear-gradient(180deg,rgba(252,254,255,0.72),rgba(243,248,250,0.84))] p-5 shadow-[0_8px_18px_rgba(106,138,151,0.04)] backdrop-blur"
            >
              <div className="font-display text-[1.6rem] text-[#1f2b31]">{value}</div>
              <div className="mt-2 text-[7px] uppercase tracking-[0.4em] text-soft/38">{label}</div>
            </div>
          ))}
        </div>

        <div className="mt-8 rounded-[2.4rem] border border-[#dce8ee] bg-[linear-gradient(180deg,rgba(252,254,255,0.78),rgba(240,246,249,0.88))] p-5 shadow-[0_10px_20px_rgba(106,138,151,0.04)] backdrop-blur">
          <div className="text-[7px] uppercase tracking-[0.4em] text-soft/42">{labels.filters}</div>
          <div className="mt-4 flex flex-wrap gap-2.5">
            {categoryGroups.map((category) => (
              <span
                key={category}
                className="rounded-full bg-[rgba(252,255,255,0.74)] px-3.5 py-2 text-[7px] uppercase tracking-[0.34em] text-soft/48 shadow-[0_6px_14px_rgba(106,138,151,0.03)]"
              >
                {category}
              </span>
            ))}
          </div>
        </div>

        <div className="mt-12 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {orderedBrandDirectory.map((brand) => (
            (() => {
              const usesPlaceholderVisual = brand.heroImage.includes("/brand-placeholders/");
              const isReferenceBrand = usesPlaceholderVisual && !brand.officialUrl;
              const usesContainedVisual =
                usesPlaceholderVisual ||
                brand.heroImage.includes("-logo") ||
                brand.heroImage.includes("/yomei-materials/") ||
                brand.heroImage.includes("/mihwanghoo-materials/");

              return (
                <a
                  key={brand.slug}
                  href={buildLocalizedPath(locale, `/brands/${brand.slug}`)}
                  className={`group overflow-hidden rounded-[2.55rem] border p-4 shadow-[0_12px_24px_rgba(106,138,151,0.05)] transition hover:-translate-y-1 hover:shadow-[0_18px_30px_rgba(106,138,151,0.08)] ${
                    isReferenceBrand
                      ? "border-[#e3ecef] bg-[linear-gradient(180deg,rgba(248,251,252,0.98),rgba(243,247,249,0.99))]"
                      : "border-[#dce8ee] bg-[linear-gradient(180deg,rgba(252,254,255,0.92),rgba(240,246,249,0.95))]"
                  }`}
                >
                  <div className="overflow-hidden rounded-[2.05rem] bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(242,248,251,0.94))]">
                    <div className="relative aspect-[1.18/0.92]">
                      {usesPlaceholderVisual ? (
                        <div className="flex h-full w-full items-center justify-center px-6 py-7">
                          <div className="w-full max-w-[15rem]">
                            <div className="rounded-[1.75rem] border border-[#e2ebf0] bg-[linear-gradient(180deg,rgba(252,254,255,0.98),rgba(244,249,251,0.98))] px-4 py-5 shadow-[0_8px_18px_rgba(106,138,151,0.04)]">
                              <div className="text-center text-[7px] uppercase tracking-[0.38em] text-soft/40">
                                {labels.brandReference}
                              </div>
                              <div className="mt-4">
                                <BrandMark name={brand.logoName} />
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <img
                          src={brand.heroImage}
                          alt={brand.name}
                          className={`h-full w-full ${brand.heroFit === "contain" || usesContainedVisual ? "object-contain p-7" : "object-cover object-center"}`}
                        />
                      )}
                      <div className="absolute left-4 top-4 rounded-full border border-white/32 bg-[rgba(255,255,255,0.2)] px-3 py-1.5 text-[7px] uppercase tracking-[0.34em] text-white/84 backdrop-blur">
                        {isReferenceBrand ? labels.brandReference : labels.featuredVisual}
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-between gap-3">
                    <div className="text-[8px] uppercase tracking-[0.32em] text-soft/40">
                      {brand.officialLabel ?? (isReferenceBrand ? labels.brandReference : labels.portfolioBrand)}
                    </div>
                    <div className={`text-[7px] uppercase tracking-[0.36em] ${isReferenceBrand ? "text-soft/28" : "text-soft/34"}`}>
                      {brand.officialUrl ? labels.portfolioBrand : labels.brandReference}
                    </div>
                  </div>
                  <div className="mt-4">
                    <BrandMark name={brand.logoName} />
                  </div>
                  <p className={`mt-4 line-clamp-2 min-h-[3.2rem] max-w-[20rem] text-[0.84rem] leading-7 ${isReferenceBrand ? "text-soft/50" : "text-soft/54"}`}>{brand.summary}</p>
                  <div className="mt-6 flex flex-wrap gap-2.5">
                    <span className="rounded-full bg-[rgba(252,255,255,0.78)] px-3 py-1.5 text-[7px] uppercase tracking-[0.34em] text-soft/44">
                      {brand.markets.slice(0, 2).join(" / ")}
                    </span>
                    <span className="rounded-full bg-[rgba(252,255,255,0.78)] px-3 py-1.5 text-[7px] uppercase tracking-[0.34em] text-soft/44">
                      {brand.category}
                    </span>
                  </div>
                  <div className="mt-5 text-[7px] uppercase tracking-[0.4em] text-soft/38 group-hover:text-[#7fa7b4]">
                    {labels.view}
                  </div>
                </a>
              );
            })()
          ))}
        </div>
      </div>
    </main>
  );
}
